import { Dropdown } from 'antd';
import { CheckOutlined, ClusterOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import NodeLogo from './NodeLogo';
import { useMyNodes } from '../../hooks/use_my_nodes';
import { useNodeInfo } from '../../hooks/use_node_info';

/**
 * The nav bar's logo, and behind it the nodes this person belongs to
 * (GET /api/nodes/mine): the current one, the others — each on its own
 * address, so switching is a full navigation — and, on a platform node with
 * self-service on, "New node". Someone with one node and nowhere to create
 * another sees the plain logo, which goes home as it always has.
 * `label` (the nav passes the node's name) sits beside the logo, inside the
 * same click target.
 */
export function NodeSwitcher({ size = 20, className, onHome, label }) {
    const navigate = useNavigate();
    const { nodes } = useMyNodes();
    const { featureSelfServiceNodes, nodeIsPlatform } = useNodeInfo();
    const canCreate = featureSelfServiceNodes && nodeIsPlatform;

    if (nodes.length <= 1 && !canCreate) {
        if (!label) return <NodeLogo size={size} className={className} onClick={onHome} />;
        return (
            <span
                className={className}
                role="button"
                onClick={onHome}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            >
                <NodeLogo size={size} />
                {label}
            </span>
        );
    }

    const items = nodes.map(node => ({
        key: node.slug,
        label: node.name,
        icon: node.is_current ? <CheckOutlined /> : <ClusterOutlined />,
        onClick: () => {
            if (node.is_current) onHome?.();
            else if (node.url) window.location.assign(node.url);
        },
    }));
    if (canCreate) {
        if (items.length) items.push({ type: 'divider' });
        items.push({ key: 'new-node', label: 'New node…', icon: <PlusOutlined />, onClick: () => navigate('/nodes/new') });
    }

    return (
        <Dropdown menu={{ items }} trigger={['click']} placement="bottomLeft">
            <span
                className={className}
                role="button"
                aria-label="Switch node"
                style={{ display: 'inline-flex', alignItems: 'center', gap: label ? 8 : 4, cursor: 'pointer' }}
            >
                <NodeLogo size={size} />
                {label}
                <DownOutlined style={{ fontSize: 10, color: 'var(--text-secondary)' }} />
            </span>
        </Dropdown>
    );
}

export default NodeSwitcher;
